import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { getIn } from 'formik'

import AdFileUpload from '../../../../../../../ReusableFormFields/AdFileUpload'

import { useMediaUpload } from '../../../../../../../ReusableFormFields/AdFileUpload/hooks/useMediaUpload'
import { formatMediaFile } from '../../../Routes/CreativeFileUploadRoute/formatters'
import { getMultipleMediaUploadsValues } from '../../../../../../../ReusableFormFields/AdFileUpload/helpers'

import { fileUpload, multipleFilesUpload } from '../../../../../../../../modules/actions/files'
import { selectedDV360AdAccountSelector } from '../../../../../../../../modules/selectors/adAccounts'

import { DV_360_PLATFORM } from '../../../../../../../../constants/selectLists/platformList'
import { ASSET_ID } from '../../../../../../../Google/AdForms/fields'
import {
  MEDIA_FILE_UPLOAD,
  FILE_NAME,
  AD_FILE,
  FILE_URL
} from '../../../../../../../ReusableFormFields/AdFileUpload/fields'
import { initialDv360File, MEDIA_ITEMS } from '../../../Routes/CreativeFileUploadRoute/fields'

import useStyles from './styles'

function Dv360MediaUpload({ formik, itemPath, onMediaRemove, index, fileValidationSchema, allowMultipleFiles }) {
  const dispatch = useDispatch()
  const classes = useStyles()
  const { setFieldValue, values } = formik

  const { id: selectedAdAccountId } = useSelector(selectedDV360AdAccountSelector)

  const { fileURLKey, fileTypeKey, uploadedMediaTypeKey, mediaFile, mediaMissingError, handleMediaRemove } =
    useMediaUpload({
      formik,
      itemPath,
      onMediaRemove,
      initialMediaFileValues: initialDv360File,
      errorFieldName: ASSET_ID
    })

  const handleSingleMediaStartUpload = useCallback(
    (file, newMediaItemValues) => {
      // once new file or files are uploaded this values should be updated with the first file in "files" array
      const currentMediaUpdatedValues = formatMediaFile(newMediaItemValues)

      // single media file was uploaded
      const currentMediaValues = getIn(values, itemPath)
      // update current media with new file values
      setFieldValue(itemPath, { ...currentMediaValues, ...currentMediaUpdatedValues })

      dispatch(
        fileUpload(file, {
          isPlatformRelated: true,
          platform: DV_360_PLATFORM,
          isPrivate: false,
          account: selectedAdAccountId
        })
      )
    },
    [dispatch, setFieldValue, selectedAdAccountId, values, itemPath]
  )

  const handleMediasStartUpload = useCallback(
    (files, newMediaItemsValues) => {
      const currentMediasValues = getIn(values, MEDIA_ITEMS)

      // this component is shown by initial media item values which added to formik values
      // once new file or files are uploaded this values should be updated with the first file in "files" array
      const updatedMediasValues = getMultipleMediaUploadsValues({
        files,
        newMediaItemsValues,
        currentMediasValues,
        index,
        initialFileValues: initialDv360File,
        formatMediaFile
      })

      setFieldValue(MEDIA_ITEMS, updatedMediasValues)

      // upload multiple files to server
      dispatch(
        multipleFilesUpload(files, {
          isPlatformRelated: true,
          platform: DV_360_PLATFORM,
          isPrivate: false,
          account: selectedAdAccountId
        })
      )
    },
    [dispatch, values, setFieldValue, selectedAdAccountId, index]
  )

  const handleMediaUploaded = useCallback(
    uploadedFile => {
      const assetFileFields = {
        ...mediaFile,
        [ASSET_ID]: uploadedFile.mediaId,
        [FILE_URL]: uploadedFile.url
      }
      setFieldValue(itemPath, assetFileFields)
    },
    [itemPath, setFieldValue, mediaFile]
  )

  return (
    <div className={classes.fileContainer}>
      <AdFileUpload
        values={formik.values}
        fileValidationSchema={fileValidationSchema}
        onFileStartUpload={allowMultipleFiles ? handleMediasStartUpload : handleSingleMediaStartUpload}
        onFileUploaded={handleMediaUploaded}
        onFileRemove={handleMediaRemove}
        error={mediaMissingError}
        mediaType={MEDIA_FILE_UPLOAD}
        fileKey={`${itemPath}[${AD_FILE}]`}
        fileNameKey={`${itemPath}[${FILE_NAME}]`}
        fileURLKey={fileURLKey}
        uploadedMediaTypeKey={uploadedMediaTypeKey}
        fileTypeKey={fileTypeKey}
        accept="image/png,image/jpg,image/jpeg,image/gif"
        maxSize={5242880}
        multiple={allowMultipleFiles}
        isDraggable
      />
    </div>
  )
}

Dv360MediaUpload.propTypes = {
  formik: PropTypes.object.isRequired,
  itemPath: PropTypes.string.isRequired,
  onMediaRemove: PropTypes.func
}

export default Dv360MediaUpload
